import { ValueObject } from 'helpers/domain'
import { RoleVO } from 'helpers/domain/shared/role.vo'

import {
  OpenEnrollmentCustomerConfigs,
  OpenEnrollmentFeatureFlags,
  OpenEnrollmentSection,
  OpenEnrollmentSectionEnum,
} from '../enums/open-enrollment.enums'

export type OpenEnrollmentAccessProps = {
  role: RoleVO
  featureFlags: OpenEnrollmentFeatureFlags
  customerConfigs: OpenEnrollmentCustomerConfigs
  // EMS-10967: false hides the People tab (US-restricted data).
  canViewUsRestrictedData: boolean
}

// PLANS is permanently hidden (EMS-9422) — encoding the exclusion in the
// return type lets every consumer rely on the compiler instead of a runtime check.
export type RenderableOpenEnrollmentSection = Exclude<
  OpenEnrollmentSection,
  typeof OpenEnrollmentSectionEnum.PLANS
>
export class OpenEnrollmentAccessVO extends ValueObject<OpenEnrollmentAccessProps> {
  protected validate(): void {}

  protected getEqualityComponents(): unknown[] {
    return [
      this._value.role.value,
      this._value.featureFlags,
      this._value.customerConfigs,
      this._value.canViewUsRestrictedData,
    ]
  }

  static create(props: OpenEnrollmentAccessProps): OpenEnrollmentAccessVO {
    return new OpenEnrollmentAccessVO(props)
  }

  get role(): RoleVO {
    return this._value.role
  }

  get featureFlags(): OpenEnrollmentFeatureFlags {
    return this._value.featureFlags
  }

  get customerConfigs(): OpenEnrollmentCustomerConfigs {
    return this._value.customerConfigs
  }

  get canViewUsRestrictedData(): boolean {
    return this._value.canViewUsRestrictedData
  }

  shouldShowOpenEnrollmentMenu(): boolean {
    return this.hasAccess()
  }

  hasAccess(): boolean {
    if (this.role.isPlatformHixmeAdmin()) {
      return this.featureFlags.isOpenEnrollmentFeatureEnabled
    }

    if (this.role.isClientAdmin()) {
      return this.isClientAdminAccessGranted()
    }

    return false
  }

  listAccessibleSections(): readonly RenderableOpenEnrollmentSection[] {
    if (this.role.isPlatformHixmeAdmin()) {
      const sections: RenderableOpenEnrollmentSection[] = [
        OpenEnrollmentSectionEnum.POLICY,
        OpenEnrollmentSectionEnum.CONTRIBUTIONS,
      ]

      if (this.canViewUsRestrictedData) {
        sections.push(OpenEnrollmentSectionEnum.PEOPLE)
      }

      // Plans tab is permanently hidden (EMS-9422) — no longer tied to this flag.
      if (this.featureFlags.isApprovalTabEnabled) {
        sections.push(OpenEnrollmentSectionEnum.APPROVALS)
      }

      return sections
    }

    if (!this.role.isClientAdmin() || !this.isClientAdminAccessGranted()) {
      return []
    }

    return this.buildClientAdminSections()
  }

  private isClientAdminAccessGranted(): boolean {
    return (
      this.featureFlags.isOpenEnrollmentFeatureEnabled &&
      this.customerConfigs.isOpenEnrollmentEnabled
    )
  }

  private buildClientAdminSections(): RenderableOpenEnrollmentSection[] {
    const sections: RenderableOpenEnrollmentSection[] = [
      OpenEnrollmentSectionEnum.POLICY,
    ]

    sections.push(OpenEnrollmentSectionEnum.CONTRIBUTIONS)

    if (
      this.customerConfigs.isPeopleTabEnabled &&
      this.canViewUsRestrictedData
    ) {
      sections.push(OpenEnrollmentSectionEnum.PEOPLE)
    }

    // Plans tab is permanently hidden (EMS-9422) — no longer tied to this flag
    // or to isPlansTabEnabled.
    if (this.featureFlags.isApprovalTabEnabled) {
      sections.push(OpenEnrollmentSectionEnum.APPROVALS)
    }

    return sections
  }
}
